import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import {
    Box,
    Heading,
    Icons,
    Button,
    Stack,
    Inline,
    useId,
    Placeholder,
    PlaceholderText,
} from 'sarsaparilla';

import ConfirmDeleteModal from './ConfirmDeleteModal';

export function OrderDetailsSection({
    children,
    heading,
    isOpen = true,
    onModifyClick,
    onRemoveClick,
    isLoading,
    modifyText = 'Modify',
}) {
    const [isExpanded, setExpanded] = useState(isOpen);
    const [isConfirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const id = useId();

    useEffect(() => {
        setExpanded(isOpen);
    }, [isOpen]);

    const hasControlButtons = !!onModifyClick || !!onRemoveClick;

    if (isLoading) {
        return <OrderDetailsSectionPlaceholder />;
    }

    return (
        <section data-component="OrderDetailsSection">
            <Box
                background="light-blue"
                border="blue"
                padding="sm"
                alignX="space-between"
                alignY="center"
            >
                <Heading headingLevel={2} appearance="h5">
                    <button
                        aria-controls={id}
                        aria-expanded={isExpanded}
                        data-shared-ods-accordion-trigger
                        onClick={() => setExpanded(prev => !prev)}
                    >
                        <span>
                            {isExpanded && <Icons.IconChevronDown size="md" />}
                            {!isExpanded && <Icons.IconChevronRight size="md" />}
                            <span>{heading}</span>
                        </span>
                    </button>
                </Heading>
                {hasControlButtons && (
                    <Inline noWrap space={['sm', 'sm', 'lg']}>
                        {!!onModifyClick && (
                            <Button
                                aria-label={`${modifyText}: ${heading}`}
                                size="sm"
                                iconBeforeElement={<Icons.IconEdit />}
                                appearance="link"
                                onClick={onModifyClick}
                            >
                                <span className="shared-hide-in-small">{modifyText}</span>
                            </Button>
                        )}
                        {!!onRemoveClick && (
                            <Button
                                aria-label={`Remove ${heading}`}
                                size="sm"
                                iconBeforeElement={<Icons.IconDelete />}
                                appearance="link"
                                onClick={() => setConfirmDeleteOpen(true)}
                            >
                                <span className="shared-hide-in-small">Remove</span>
                            </Button>
                        )}
                    </Inline>
                )}
            </Box>
            {isExpanded && (
                <Box id={id} padding="md" border="gray" className="shared-expand-section">
                    <Stack>{children}</Stack>
                </Box>
            )}

            <ConfirmDeleteModal
                isOpen={isConfirmDeleteOpen}
                onClose={() => {
                    setConfirmDeleteOpen(false);
                }}
                onRemoveClick={onRemoveClick}
            />
        </section>
    );
}

OrderDetailsSection.propTypes = {
    children: PropTypes.node,
    heading: PropTypes.node,
    isOpen: PropTypes.bool,
    isLoading: PropTypes.bool,
    onRemoveClick: PropTypes.func,
    onModifyClick: PropTypes.func,
    modifyText: PropTypes.node,
};

function OrderDetailsSectionPlaceholder() {
    return (
        <section data-component="OrderDetailsSection">
            <Box border="gray">
                <Placeholder height="5rem" />
            </Box>
            <Box padding="md" border="gray" className="shared-expand-section">
                <Stack>
                    <PlaceholderText lines={3} />
                    <PlaceholderText lines={2} />
                </Stack>
            </Box>
        </section>
    );
}
